import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { Audit } from '../models/audit';
import type { AuditDownloadSelection, AuditPageRequest, PagedResult } from '../models/paged-result';
import type { DownloadJobStatus } from '../models/download-job';

@Injectable({
  providedIn: 'root'
})
export class AuditService {
    apiUrl = "api/audit";
    constructor(private dataService: DataService) {

    }

    async getAuditDetails(
        requestId: number | undefined,
        requestType: string | undefined,
        hasAccessToViewLogs: boolean,
        userId: string | ''
    ): Promise<Audit[]> {
        let url = "/list-audits";
        const params: string[] = [];
        params.push(`hasAccessToViewLogs=${hasAccessToViewLogs}`);
        params.push(`userId=${encodeURIComponent(userId)}`);

        if (requestId !== undefined) {
            params.push(`requestId=${requestId}`);
        }
        if (requestType !== undefined) {
            params.push(`requestType=${encodeURIComponent(requestType)}`);
        }

        url += `?${params.join("&")}`;

        return await this.dataService.get<Audit[]>(this.apiUrl + url);
    }

    async getUserAudit(userId: string, jurisdictionOrgKey: string): Promise<Audit[]> {
        const url = `${this.apiUrl}/user-audits?userId=${encodeURIComponent(userId)}&jurisdictionOrgKey=${encodeURIComponent(jurisdictionOrgKey)}`;
        return await this.dataService.get<Audit[]>(url);
    }

    async getUserAuditByRequest(requestId: number, requestType: number): Promise<Audit[]> {
        return await this.dataService.get<Audit[]>(
            `${this.apiUrl}/audit-history?requestId=${requestId}&requestTypeId=${encodeURIComponent(requestType)}`
        );
    }

    /** Gets one server-side page of the Audit Tab; the caller is identified by the bearer token. */
    async getUserAuditPage(request: AuditPageRequest): Promise<PagedResult<Audit>> {
        return await this.dataService.post<PagedResult<Audit>>(`${this.apiUrl}/user-audits/search`, request);
    }

    /**
     * Starts a background ZIP download of the selected requests, however many there are. The server answers at once;
     * progress and the finished ZIP are followed through the DownloadJobService.
     */
    async startDownload(selection: AuditDownloadSelection): Promise<DownloadJobStatus> {
        return await this.dataService.post<DownloadJobStatus>(`${this.apiUrl}/user-audits/downloads`, selection);
    }

    /** Gets every value of a data-driven column the caller may see, for the checkbox filter list. */
    async getUserAuditFilterValues(field: string): Promise<string[]> {
        return await this.dataService.get<string[]>(`${this.apiUrl}/user-audits/filter-values?field=${encodeURIComponent(field)}`);
    }

    async getRejectedWithdrawnCount(userId: string, jurisdictionOrgKey: string): Promise<number> {
        const url = `${this.apiUrl}/rejected-withdrawn-count?userId=${encodeURIComponent(userId)}&jurisdictionOrgKey=${encodeURIComponent(jurisdictionOrgKey)}`;

        return await this.dataService.get<number>(url);
    }
}
